"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Netflix-style horizontal shelf — a wrapper around an overflow-x row
 * that hides the native scrollbar cross-browser and renders tall
 * chevron buttons that appear on hover (desktop only). The row stays
 * a plain scrollable `<div>` so touch-drag on mobile still works and
 * assistive tech can still walk it with keyboard arrows.
 *
 * Arrows page by ~85% of the container's visible width on click,
 * smooth-scrolled. They fade in/out based on whether the row has
 * more content in that direction — so we don't flash a ">" button
 * at the far-right edge.
 *
 * Usage:
 *
 *   <HorizontalShelf>
 *     {items.map(item => <Card key={item.id} ... />)}
 *   </HorizontalShelf>
 *
 * The child `<Card>` components are responsible for their own widths
 * (min-w-[…] etc.) — this component doesn't prescribe a card shape.
 */
export function HorizontalShelf({
  children,
  className,
  edgeFadeClassName = "from-background",
}: {
  children: React.ReactNode;
  className?: string;
  /**
   * Tailwind `from-*` color used by the left/right fade gradient that
   * softens the edge under the chevron buttons. Defaults to the page
   * background; override when the shelf sits over a different tint.
   */
  edgeFadeClassName?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Recompute the at-edge flags after scrolling, resize, or child
  // mutations (new cards appended from infinite scroll would otherwise
  // leave `canScrollRight = false` even though there's more content).
  const syncEdges = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const { scrollLeft, scrollWidth, clientWidth } = node;
    // Tiny epsilon — `scrollLeft` can end up at `0.5` on trackpads.
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    syncEdges();
    const node = scrollerRef.current;
    if (!node) return;

    node.addEventListener("scroll", syncEdges, { passive: true });
    // ResizeObserver covers the "font loaded" reflow and any DOM
    // mutation inside the shelf (infinite-scroll appends, images
    // resolving, etc.).
    const observer = new ResizeObserver(syncEdges);
    observer.observe(node);
    for (const child of Array.from(node.children)) observer.observe(child);

    return () => {
      node.removeEventListener("scroll", syncEdges);
      observer.disconnect();
    };
  }, [syncEdges]);

  const paginate = (direction: "left" | "right") => {
    const node = scrollerRef.current;
    if (!node) return;
    const delta = node.clientWidth * 0.85;
    node.scrollBy({ left: direction === "left" ? -delta : delta, behavior: "smooth" });
  };

  return (
    <div className={cn("group/shelf relative", className)}>
      {/* The actual scroll container. `scrollbar-hide` is the
          cross-browser class defined in globals.css; `snap-x` keeps
          the drag-feel on touch devices. */}
      <div
        ref={scrollerRef}
        className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0"
      >
        {children}
      </div>

      {/* Left chevron — appears only on hover + when there's content
          to the left. `hidden sm:flex` keeps it off mobile where the
          drag gesture is the intended paging affordance. */}
      {canScrollLeft ? (
        <>
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 left-0 hidden w-16 bg-gradient-to-r to-transparent opacity-0 transition-opacity duration-200 group-hover/shelf:opacity-100 sm:block",
              edgeFadeClassName,
            )}
          />
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => paginate("left")}
            className="absolute inset-y-0 left-0 hidden w-12 items-center justify-center rounded-l-2xl bg-black/20 text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:bg-black/40 group-hover/shelf:opacity-100 sm:flex"
          >
            <ChevronLeft className="size-6" />
          </button>
        </>
      ) : null}

      {canScrollRight ? (
        <>
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-y-0 right-0 hidden w-16 bg-gradient-to-l to-transparent opacity-0 transition-opacity duration-200 group-hover/shelf:opacity-100 sm:block",
              edgeFadeClassName,
            )}
          />
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => paginate("right")}
            className="absolute inset-y-0 right-0 hidden w-12 items-center justify-center rounded-r-2xl bg-black/20 text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:bg-black/40 group-hover/shelf:opacity-100 sm:flex"
          >
            <ChevronRight className="size-6" />
          </button>
        </>
      ) : null}
    </div>
  );
}
